import { Badge } from '@bambi/component-library/src/components/Badge/Badge';
import { Button } from '@bambi/component-library/src/components/Button/Button';
import { TrashIcon } from '@radix-ui/react-icons';
import { DateTime } from 'luxon';

import { AttendantRead } from 'src/common/external/bambi-api/bambiApi';
import { FormField } from 'src/common/FormField';
import { FormFieldController } from 'src/common/FormField/FormFieldController';
import { MultiSelect } from 'src/common/primitives/MultiSelect/MultiSelect';
import { formatFullName } from 'src/common/util/formatFullName';

import { AssignmentTimeInputs } from './AssignmentTimeInputs';
import { CloneAssignmentAction } from './CloneAssignmentAction';
import { useAssignmentEditorViewModel } from './useAssignmentEditorViewModel/useAssignmentEditorViewModel';

type AssignmentEditorProps = {
  day: DateTime;
  index?: number;
  isAvailable?: boolean;
  isEditing?: boolean;
};

export function AssignmentEditor({
  day,
  index,
  isAvailable,
  isEditing,
}: AssignmentEditorProps) {
  const {
    isEditingExistingAssignment,
    handleDeleteAssignment,
    attendantOptions,
    attendantsData,
    fieldNameRoot,
    driverOptions,
    drivers,
    driversLoading,
    driverLocked,
    attendantsLoading,
  } = useAssignmentEditorViewModel({
    day,
  });

  if (driversLoading || attendantsLoading) {
    return <div>Loading...</div>;
  }

  if (!isAvailable) {
    return <span className="text-xs">Unavailable</span>;
  }

  if (!isEditingExistingAssignment && !isEditing) {
    return <span className="text-xs">No Schedule</span>;
  }
  return (
    <div className="flex flex-col gap-y-2">
      {!isEditingExistingAssignment && typeof index === 'number' && (
        <div
          className={`flex items-center justify-between ${index > 0 && 'pt-2'}`}
        >
          <Badge
            color="primary"
            label={`${day.weekdayLong} Assignment ${index + 1}`}
          ></Badge>
          <CloneAssignmentAction assignmentFormValuePath={fieldNameRoot} />
          <Button
            variant="ghost"
            onClick={() => handleDeleteAssignment(index)}
            dataTestId="assignment-delete-button"
          >
            <TrashIcon className="h-5 w-5 cursor-pointer text-red-500" />
          </Button>
        </div>
      )}
      <AssignmentTimeInputs />
      <div>
        <FormFieldController
          name={`${fieldNameRoot}.driver`}
          rules={{
            required: 'Driver is required',
          }}
        >
          {({ field, fieldState }) => {
            return (
              <>
                <FormField
                  type="select"
                  placeholder="Select driver"
                  inputProps={{
                    id: `${fieldNameRoot}.driver`,
                    ...field,
                  }}
                  options={driverOptions()}
                  onChange={(selected) => {
                    const driver = drivers.find(
                      (x) => x.id === selected?.value
                    );
                    field.onChange(driver);
                  }}
                  value={
                    field.value?.id
                      ? {
                          value: field.value.id,
                          label: formatFullName(field.value),
                        }
                      : undefined
                  }
                  disabled={driverLocked}
                  helperText={
                    driverLocked
                      ? {
                          text: lockedDriverHelperText,
                          presentation: {
                            type: 'tooltip',
                            position: 'absolute right-full w-[283px]',
                          },
                        }
                      : undefined
                  }
                  error={fieldState.error?.message?.toString()}
                />
              </>
            );
          }}
        </FormFieldController>
      </div>
      <div>
        <FormFieldController name={`${fieldNameRoot}.attendants`}>
          {({ field, fieldState }) => {
            return (
              <MultiSelect
                placeholder="Select attendant"
                selected={field.value?.map((x: AttendantRead) => {
                  return { value: x.id, label: formatFullName(x) };
                })}
                options={attendantOptions()}
                onChange={(selected) => {
                  const attendants = selected.map((x) => {
                    return attendantsData?.results.find(
                      (y) => y.id === x.value
                    );
                  });
                  field.onChange(attendants);
                }}
              />
            );
          }}
        </FormFieldController>
      </div>
    </div>
  );
}

const lockedDriverHelperText =
  'This assignment has already started so the driver cannot be changed. ' +
  'To change the driver, create a new assignment and transfer trips as needed.';
